import type { BusinessDailySummary } from '../../../lib/dailyApi';
import type { CheckInStatus, TeamDailyStatusReport } from '../../../lib/dailyStatusApi';
import { UserTimelineCard } from './UserTimelineCard';
import { useI18n } from '../../../contexts/I18nContext';

interface Props {
  summary: BusinessDailySummary;
  /** Today's check-ins, for the per-task badges and the "Raportat N/M" header. */
  team?: TeamDailyStatusReport;
}

export interface MemberCheckIns {
  byTask: Record<string, CheckInStatus>;
  checkedIn: number;
  total: number;
  notReported: boolean;
}

function checkInsByMember(team: TeamDailyStatusReport | undefined): Map<string, MemberCheckIns> {
  const out = new Map<string, MemberCheckIns>();
  if (!team) return out;
  for (const member of team.members) {
    const byTask: Record<string, CheckInStatus> = {};
    for (const tasks of Object.values(member.sections)) {
      for (const task of tasks) if (task.checkIn) byTask[task.id] = task.checkIn.status;
    }
    out.set(member.user.id, {
      byTask,
      checkedIn: member.summary.checkedIn,
      total: member.summary.total,
      notReported: member.notReported,
    });
  }
  return out;
}

/**
 * Business-wide ("All Users") Daily Summary view (CLE-58). Renders one card per
 * active team member with their tasks on the due-date timeline. Members who
 * have at least one task are shown first; members with no tasks are listed
 * compactly afterwards so the overview stays focused on actionable work.
 */
export function BusinessSummarySection({ summary, team }: Props) {
  const { t } = useI18n();
  const checkIns = checkInsByMember(team);
  const withTasks = summary.users.filter((u) => u.total > 0);
  const withoutTasks = summary.users.filter((u) => u.total === 0);

  return (
    <div className="space-y-6">
      {withTasks.length === 0 ? (
        <p className="text-sm text-gray-500">{t('daily.business.empty')}</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {withTasks.map((u) => (
            <UserTimelineCard key={u.user.id} summary={u} checkIns={checkIns.get(u.user.id)} />
          ))}
        </div>
      )}

      {withoutTasks.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
            {t('daily.business.noTasks', { count: withoutTasks.length })}
          </p>
          <p className="mt-1 text-sm text-gray-600">
            {withoutTasks.map((u) => u.user.name).join(', ')}
          </p>
        </div>
      )}
    </div>
  );
}
